import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faThumbsDown as faThumbsDownSolid} from '@fortawesome/free-solid-svg-icons';
import {Tooltip, Button} from 'antd';
import styled from 'styled-components';
import {faThumbsUp as faThumbUpSolid} from '@fortawesome/pro-solid-svg-icons';
import {faThumbsUp, faThumbsDown} from '@fortawesome/pro-regular-svg-icons';
import {faSquareQuestion} from '@fortawesome/pro-duotone-svg-icons';
import {useStore} from '@/store/root-store';
import {observer} from 'mobx-react-lite';

interface ThumbsRatingProps {
  selectedCategory: string;
  uuid: string;
  width?: string;
}

interface ThumbButtonProps {
  isActive: boolean;
  isUp: boolean;
  onClick: () => void;
  style?: React.CSSProperties;
}

const ThumbButton = ({isActive, isUp, onClick, style}: ThumbButtonProps) => {
  const icon = isUp ? (isActive ? faThumbUpSolid : faThumbsUp) : (isActive ? faThumbsDownSolid : faThumbsDown);
  const color = isActive ? '#2d6bd7' : '#414244';

  return (
    <StyledButton
      type='default'
      disabled={isActive}
      style={style}
      icon={<FontAwesomeIcon icon={icon} style={{color}} />}
      onClick={onClick}
    />
  );
};

export const ThumbsRating=observer(({selectedCategory, uuid, width}: ThumbsRatingProps) => {
  const {
    ottoV2Store: {postOttoFeedback, getFeedbackData, getOttoFeedback},
  } = useStore('');
  const feedbackData = getFeedbackData?.find(item => item.issueType === selectedCategory);
  const feedbackThumb = feedbackData?.feedback;

  async function postFeedback(newRating: boolean) {
    const payload = {
      issue_type: selectedCategory,
      otto_project_uuid: uuid,
      feedback: newRating ? false : true,
    };
    if (newRating === true) {
      await postOttoFeedback(payload);
      await getOttoFeedback(uuid);
    } else if (newRating === false) {
      await postOttoFeedback(payload);
      await getOttoFeedback(uuid);
    }
  }
  return (
    <Container width={width}>
      <ThumbButton
        isActive={feedbackThumb === true}
        isUp={true}
        style={{marginBottom: '2px'}}
        onClick={() => {
          const newRating = feedbackThumb === true ? true : false;
          postFeedback(newRating);
        }}
      />
      <Divider />
      <ThumbButton
        isActive={feedbackThumb === false}
        isUp={false}
        style={{marginTop: '2px'}}
        onClick={() => {
          const newRating = feedbackThumb === false ? false : true;
          postFeedback(newRating);
        }}
      />
      <Tooltip title={<><span style={{fontWeight: 'bold'}} >Is this feature helpful to you?</span><br /><span>Your feedback determines which tools we enhance in future updates.</span></>} placement='bottom' overlayInnerStyle={{backgroundColor: '#121212', borderRadius: '8px', border: '1px solid #121212', width: 310, fontSize: 12}}>
        <FontAwesomeIcon icon={faSquareQuestion} style={{'--fa-primary-color': '#a3a4a4', '--fa-secondary-color': '#949494'} as React.CSSProperties} />
      </Tooltip>
    </Container>
  );
});

const Container = styled.div<{width?: string}>`
  display: flex;
  align-items: center;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 7px 10px 8px 10px;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  gap: 4px;
  width: ${p => p?.width || '96.5px'};
  height: 32px;
  max-width: 300px;
  justify-content: space-between;

  @media (max-width: 1092px) {
    height: 28px;
    padding: 5px 8px 6px 8px;
  }

  @media (max-width: 864px) {
    height: 26px;
    padding: 4px 6px 5px 6px;
  }
`;

const Divider = styled.span`
  width: 1px;
  height: 18px;
  background: #ddd;

  @media (max-width: 1092px) {
    height: 16px;
    width: 1px;
    padding: 0px 0px 0px 1px;
  }

  @media (max-width: 864px) {
    height: 14px;
  }
`;

const StyledButton = styled(Button)`
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:focus,
  &:active,
  &:hover {
    box-shadow: none !important;
    outline: none !important;
    background: transparent !important;
  }

  @media (max-width: 1092px) {
    padding: 3px;
  }

  @media (max-width: 864px) {
    padding: 2px;
  }
  .anticon {
    @media (max-width: 1092px) {
      font-size: 14px;
    }

    @media (max-width: 864px) {
      font-size: 12px;
    }
  }
`;
